import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Trophy, Zap, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createOrLoginPlayer } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

const COUNTRIES = [
  "United States","United Kingdom","Canada","Australia","India","Germany","France","Japan",
  "Brazil","Mexico","Spain","Italy","Netherlands","Sweden","Norway","Poland","South Korea",
  "Singapore","United Arab Emirates","South Africa","Argentina","Chile","New Zealand","Other"
];

const CAR_COLORS = [
  { name: "Cyan Flux", value: "#06B6D4" },
  { name: "Volt Yellow", value: "#EAB308" },
  { name: "Pink Neon", value: "#EC4899" },
  { name: "Radioactive", value: "#22C55E" },
  { name: "Blood Orange", value: "#F97316" },
  { name: "Deep Violet", value: "#8B5CF6" },
  { name: "Ice White", value: "#F8FAFC" },
  { name: "Obsidian", value: "#27272A" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { player, login } = usePlayer();
  const [form, setForm] = useState({ name: "", email: "", age: "", country: "", car_color: CAR_COLORS[0].value });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.age || !form.country) {
      toast.error("Fill in every field to start driving");
      return;
    }
    setSubmitting(true);
    try {
      const p = await createOrLoginPlayer({
        name: form.name.trim(),
        email: form.email.trim(),
        age: Number(form.age),
        country: form.country,
        car_color: form.car_color,
      });
      login(p);
      toast.success(`Welcome, ${p.name}. Engine warming up...`);
      navigate("/play");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start session";
      toast.error(typeof msg === "string" ? msg : "Invalid details");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-[#050505] overflow-hidden" data-testid="landing-page">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1629019638491-c89d444bacaf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxuZW9uJTIwcmFjaW5nJTIwY2FyJTIwZGFya3xlbnwwfHx8fDE3ODY5NTI0OTJ8MA&ixlib=rb-4.1.0&q=85"
          alt="neon racing car"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="absolute inset-0 synth-grid opacity-40" />
      </div>

      <nav className="relative z-20 flex items-center justify-between px-8 md:px-16 py-6" data-testid="landing-nav">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-cyan-400 shadow-[0_0_12px_#06B6D4] animate-flicker" />
          <span className="font-display font-black tracking-tighter text-xl text-white uppercase">
            Neon<span className="text-cyan-400 text-glow-cyan">/</span>Drive
          </span>
        </div>
        <div className="flex items-center gap-6 font-mono text-xs uppercase tracking-widest text-zinc-400">
          <Link to="/leaderboard" className="hover:text-cyan-400 transition-colors" data-testid="nav-leaderboard-link">Leaderboard</Link>
          {player && (
            <Link to="/profile" className="hover:text-cyan-400 transition-colors" data-testid="nav-profile-link">
              {player.name}
            </Link>
          )}
        </div>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-16 pt-8 md:pt-16 pb-24 grid grid-cols-1 md:grid-cols-12 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="md:col-span-8 flex flex-col justify-center"
        >
          <div className="inline-flex items-center gap-2 mb-8 px-3 py-1 border border-cyan-500/40 bg-cyan-500/5 w-fit" data-testid="hero-badge">
            <Zap className="w-3 h-3 text-cyan-400" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Free · Browser · No install</span>
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl lg:text-8xl leading-[0.9] text-white" data-testid="hero-title">
            Drive the<br/>
            <span className="text-cyan-400 text-glow-cyan">neon</span> horizon<br/>
            <span className="text-zinc-500">— no</span> keys<span className="text-zinc-500"> required.</span>
          </h1>
          <p className="mt-8 max-w-xl font-mono text-sm md:text-base text-zinc-400 leading-relaxed" data-testid="hero-subtitle">
            A 3D free-drive playground rendered in your browser. Pick your ride, chase glowing coins,
            climb the global ladder — every second driven counts toward your rank.
          </p>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { icon: Timer, label: "Session Tracking", value: "Live" },
              { icon: Trophy, label: "Global Ranking", value: "Top 25" },
              { icon: Zap, label: "3D Graphics", value: "WebGL" },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="border border-zinc-800 p-4 bg-black/40 backdrop-blur-sm">
                <Icon className="w-4 h-4 text-cyan-400 mb-3" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
                <div className="font-display font-bold text-lg text-white">{value}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="md:col-span-4"
        >
          <div className="relative bg-black/60 backdrop-blur-xl border border-white/10 p-8 border-glow-cyan" data-testid="guest-login-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400">/// START_ENGINE</div>
                <h2 className="font-display font-bold text-2xl text-white mt-1">Guest Access</h2>
              </div>
              <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_10px_#06B6D4]" />
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="guest-login-form">
              <div>
                <Label htmlFor="name" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Driver Name</Label>
                <Input id="name" data-testid="input-name" value={form.name} onChange={(e) => onChange("name", e.target.value)}
                  className="mt-2 bg-zinc-950 border-zinc-800 font-mono text-white focus:ring-cyan-500 focus:border-cyan-500 rounded-none" placeholder="Alex Rider" />
              </div>
              <div>
                <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Email</Label>
                <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={(e) => onChange("email", e.target.value)}
                  className="mt-2 bg-zinc-950 border-zinc-800 font-mono text-white focus:ring-cyan-500 focus:border-cyan-500 rounded-none" placeholder="alex@drive.io" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="age" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Age</Label>
                  <Input id="age" type="number" min="6" max="120" data-testid="input-age" value={form.age} onChange={(e) => onChange("age", e.target.value)}
                    className="mt-2 bg-zinc-950 border-zinc-800 font-mono text-white focus:ring-cyan-500 focus:border-cyan-500 rounded-none" placeholder="24" />
                </div>
                <div>
                  <Label htmlFor="country" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Country</Label>
                  <select id="country" data-testid="input-country" value={form.country} onChange={(e) => onChange("country", e.target.value)}
                    className="mt-2 h-10 w-full bg-zinc-950 border border-zinc-800 font-mono text-white text-sm px-3 rounded-none focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
                    <option value="">Select...</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Car Colour</Label>
                <div className="mt-2 grid grid-cols-4 gap-2" data-testid="car-color-picker">
                  {CAR_COLORS.map((c) => {
                    const active = form.car_color === c.value;
                    return (
                      <button
                        type="button"
                        key={c.value}
                        onClick={() => onChange("car_color", c.value)}
                        title={c.name}
                        data-testid={`car-color-${c.value.replace("#", "").toLowerCase()}`}
                        className={`relative h-10 rounded-none transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-cyan-400 ${active ? "ring-2 ring-cyan-400" : "ring-1 ring-white/10"}`}
                        style={{ background: c.value, boxShadow: active ? `0 0 12px ${c.value}` : "none" }}
                      >
                        {active && (
                          <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] font-bold text-black/80">SEL</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Button type="submit" disabled={submitting} data-testid="start-driving-button"
                className="w-full mt-4 rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-display font-bold uppercase tracking-widest h-12 border-glow-cyan transition-transform hover:-translate-y-0.5 active:translate-y-0">
                {submitting ? "Igniting..." : (<span className="inline-flex items-center gap-2">Start Driving <ArrowRight className="w-4 h-4" /></span>)}
              </Button>

              <p className="font-mono text-[10px] text-zinc-500 leading-relaxed pt-2">
                No password. Your session time is tracked and contributes to the global leaderboard.
              </p>
            </form>
          </div>
        </motion.div>
      </div>

      <footer className="relative z-10 border-t border-zinc-900 py-6 px-8 md:px-16 flex justify-between items-center">
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© Neon/Drive — Emergent</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">v1.1 · WebGL 2</span>
      </footer>
    </div>
  );
}
